import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useBoatActions } from '@/entities/boat';
import { useHistoricalDataActions } from '@/entities/historical-data';
import { useCommonModal } from '@/entities/modal';
import { useCreateHistogramFormActions } from '@/features/histogram-form';
import { AppRoutes } from '@/shared/constants/app-routes.constant';
import { SearchParams } from '@/shared/constants/search-params.constant';
import { useNamespace } from '@/shared/lib/hooks/use-namespace.hook';

import { SELECT_DEFAULT_VALUE } from '../../constants/select-default.constant';

const getRouteDetails = (): [string, string[]] => {
	const splittedRoute = window.location.pathname.split('/');
	const routeBoatId = splittedRoute[splittedRoute.length - 1];

	if (routeBoatId && Number(routeBoatId)) {
		return [routeBoatId, splittedRoute];
	}

	return ['', splittedRoute];
};

interface UseUpdateBoatIdArgs {
	redirect: boolean;
}

export const useUpdateBoatId = ({ redirect }: UseUpdateBoatIdArgs) => {
	const namespace = useNamespace('common');
	const { t } = useTranslation(namespace as ['plant', 'common'] | 'common');
	const navigate = useNavigate();
	const { resetHistogram } = useCreateHistogramFormActions();
	const { resetData } = useHistoricalDataActions();
	const { setBoatById, resetBoatById } = useBoatActions();
	const [openModal, hideModal] = useCommonModal();
	const [routeBoatId, splittedRoute] = getRouteDetails();
	const [, setSearchParams] = useSearchParams();

	return useCallback(
		(id: string) => {
			const isDefault = id === SELECT_DEFAULT_VALUE;

			if (routeBoatId && routeBoatId !== id) {
				const currentRoute = splittedRoute.reduce(
					(route, value) => (Number(value) > 0 ? route : (route += `${value}/`)),
					'',
				);
				navigate(`${currentRoute}/${id}`);
			}

			if (redirect && isDefault) {
				openModal({
					title: t('modal.single.title'),
					description: t('modal.redirect.all.description'),
					onConfirm: () => {
						hideModal();
						resetBoatById();
						navigate(AppRoutes.Home);
					},
				});
				return;
			}
			if (isDefault) {
				resetBoatById();
			} else {
				setBoatById(id);
				resetHistogram();
				resetData();
				setSearchParams((params) => {
					params.delete(SearchParams.Zoom);
					params.delete(SearchParams.Lat);
					params.delete(SearchParams.Lng);
					params.delete(SearchParams.YachtTrack);
					return params;
				});
			}
		},
		[
			routeBoatId,
			redirect,
			splittedRoute,
			navigate,
			openModal,
			hideModal,
			t,
			resetBoatById,
			setBoatById,
			resetHistogram,
			setSearchParams,
		],
	);
};
